import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Root } from '~/training/sequence/templates/items/root/Root';
import { translate } from '~/training/sequence/templates/index';
import { MediaPlayerProvider } from '~/ui/video-player/MediaPlayerProvider';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { SequenceProvider } from '~/training/sequence/SequenceStore';
import { Spinner } from '~/ui/spinner/Spinner';
import { ComboButton } from '~/training/sequence/ComboButton';
import { TenSecsBack } from '~/ui/video-player/TenSecsBack';
import { ReplayMediaBtn } from '~/ui/video-player/ReplayMediaBtn';

const Wrapper = styled.div`
  color: #444746;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

type SequenceData = Record<string, unknown>;

type PreviewMessage =
  | { type: 'PREVIEW_DATA'; payload: SequenceData }
  | { type: string; payload?: unknown };

const SequenceRenderer = ({ data }: { data: SequenceData }) => {
  // Errors thrown synchronously here (invalid template name, missing fields, …)
  // are caught by the parent <PreviewErrorBoundary>.
  const RootComponent = new Root(
    translate(data as unknown as Parameters<typeof translate>[0])
  ).component();

  return (
    <MediaPlayerProvider>
      <AudioPlayer />
      <SequenceProvider>
        <RootComponent key={`editor_${data.id}`} />
      </SequenceProvider>
      <ComboButton hideNext={true} />
      <TenSecsBack />
      <ReplayMediaBtn />
    </MediaPlayerProvider>
  );
};

interface PreviewErrorBoundaryProps {
  onError: (err: Error) => void;
  fallback: React.ReactNode;
  children: React.ReactNode;
}

interface PreviewErrorBoundaryState {
  hasError: boolean;
}

class PreviewErrorBoundary extends React.Component<
  PreviewErrorBoundaryProps,
  PreviewErrorBoundaryState
> {
  state: PreviewErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError(error);
    console.error('[Preview] Render error:', error, info.componentStack);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export const Preview = () => {
  const [data, setData] = useState<SequenceData | null>(null);
  const [revision, setRevision] = useState(0);

  const reportError = useCallback((err: Error) => {
    try {
      // codeql[js/cross-window-information-leak] — see .github/CODEQL.md
      window.opener?.postMessage(
        {
          type: 'PREVIEW_ERROR',
          error: {
            message: err?.message || String(err),
            stack: err?.stack
          }
        },
        // codeql[js/cross-window-information-leak]
        '*'
      );
    } catch {
      // opener may be cross-origin or already closed; ignore
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<PreviewMessage>) => {
      const msg = event?.data;
      if (msg?.type === 'PREVIEW_DATA' && msg.payload) {
        setData(msg.payload as SequenceData);
        setRevision(r => r + 1);
      }
    };

    window.addEventListener('message', handleMessage);

    try {
      // codeql[js/cross-window-information-leak] — see .github/CODEQL.md
      window.opener?.postMessage(
        { type: 'PREVIEW_READY' },
        // codeql[js/cross-window-information-leak]
        '*'
      );
    } catch {
      // opener may be cross-origin or already closed; ignore
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <Wrapper>
      {data ? (
        <PreviewErrorBoundary
          // remount on every new payload so a previously failed render
          // doesn't keep the spinner stuck for a fresh, valid one
          key={`boundary_${revision}`}
          onError={reportError}
          fallback={<Spinner />}
        >
          <SequenceRenderer data={data} />
        </PreviewErrorBoundary>
      ) : (
        <Spinner />
      )}
    </Wrapper>
  );
};
